/* eslint-disable max-lines */
import { notificationsPopoverOpenAtom } from '@/atoms/sideNavAtoms';
import {
  IS_EVIDENCE_LEADERBOARD_ENABLED,
  IS_GERMAN_GET_STARTED_CTA,
  IS_GET_HEIDI_FOR_MOBILE_ENABLED,
  IS_MD_WIDGET_LAUNCH_ENABLED,
} from '@/components/constants/flags';
import { isMobileHeaderSheetOpenAtom } from '@/components/layout/header/mobile-app-header/mobile-header-sheet-atoms';
import { offlineGatingMessages } from '@/components/offline-recording/offline-gating-messages';
import {
  useIsCpdAccountMenuShown,
  useIsCpdQueryEnabled,
  useIsCpdUsUser,
} from '@/hooks/useIsCpdSurfaceEnabled';
import { navigationLogout } from '@/lib/analytics/scribe/auth';
import { navigationGetStartedFAQ } from '@/lib/analytics/scribe/navigation';
import { logout } from '@/lib/authActions';
import { Badge } from '@heidi/design-system/badge';
import { DropdownMenuItem } from '@heidi/design-system/dropdown-menu';
import { SidebarMenu, UserAccountMenu } from '@heidi/design-system/page-shell';
import { Popover } from '@heidi/design-system/popover';
import { Separator } from '@heidi/design-system/separator';
import { Tooltip, TooltipContent, TooltipTrigger } from '@heidi/design-system/tooltip';
import { useCPDTerm } from '@heidi/evidence';
import { useQueryCPDStats } from '@heidi/evidence/data';

import { AppearanceModeMenuItems } from '@/components/layout/Navigation/AppearanceModeMenuItems';
import { ReferralButton } from '@/components/layout/Navigation/ReferralButton';
import { TrialCountdownButton } from '@/components/layout/Navigation/TrialCountdownButton';
import { isEvidenceReferralModalOpenAtom } from '@/components/modal/evidence-referral-modal/index.atoms';
import UpgradeCTAButton from '@/components/ui/button/upgrade-cta-button/upgrade-cta-button';
import {
  useComplianceNotificationFlagGates,
  useIsRibbonSidebarEnabled,
  useIsSidebarIAEnabled,
  useIsWorkspaceSettingsIaEnabled,
} from '@/features/flags';
import {
  RemoteIntroPromoMenuCard,
  RemoteIntroPromoModal,
  useIsRemoteIntroPromoMenuCardVisible,
} from '@/features/growth/remote-intro-promo';
import { useEffectiveSessionTimeout } from '@/features/settings/account/components/security/session-timeout-state';
import { useSettingsModal } from '@/features/settings/hooks/useSettingsModal';
import { useOfflineGating } from '@/hooks/desktop/useOfflineGating';
import type MedicalDirectorWidgetLaunchButtonType from '@/integrations/medical-director/medical-director-widget-launch';
import { cn } from '@/lib/cn';
import {
  isCjkLocale,
  mapLocaleAliasToSupportedLocale,
} from '@/utils/i18n/supportedLocales';
import {
  isGetMobileAppModalOpenAtom,
  useAutoOpenComplianceNotificationsOnLogin,
} from '@heidi/growth';
import { useAtom, useSetAtom } from 'jotai';
import {
  BookOpenIcon,
  Building,
  Gift,
  GraduationCap,
  LogOut,
  Settings,
  Smartphone,
} from 'lucide-react';
import dynamic from 'next/dynamic';
import { useRouter } from 'next/router';
import {
  memo,
  useCallback,
  useMemo,
  type ComponentProps,
  type ComponentType,
} from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { DiscoveryCallNavItem } from './discovery-call-nav-item';
import { FooterIconButtons } from './FooterIconButtons';
import { useEvidenceNavEnabled } from './hooks/useEvidenceNavEnabled';
import { useIsRibbonRail } from './hooks/useIsRibbonRail';
import { useMobilePanel } from './mobile/use-mobile-panel';
import { NotificationsPopoverContent } from './notifications/notifications-popover';
import { SessionLayoutV2MenuToggle } from './SessionLayoutV2MenuToggle';
import { FooterRailCollapsibleItem } from './sidebar/footer-rail/footer-rail-collapsible-item';
import { type FooterMoreRowDescriptor } from './sidebar/footer-rail/footer-rail-context';
import { FooterRailMore } from './sidebar/footer-rail/footer-rail-more';
import { FooterRailProvider } from './sidebar/footer-rail/footer-rail-provider';
import { IntegrationsNavItem } from './sidebar/nav-items/IntegrationsNavItem';
import { sideNavFooterMessages } from './SideNavFooter.messages';
import { SideNavFooterUpgradeSlot } from './SideNavFooterUpgradeSlot';

const MedicalDirectorWidgetLaunchButton: ComponentType<
  ComponentProps<typeof MedicalDirectorWidgetLaunchButtonType>
> = dynamic(
  () => import('../../../integrations/medical-director/medical-director-widget-launch'),
  {
    ssr: false,
  }
);

const GET_STARTED_FAQ_URL =
  'https://heidihealth.notion.site/Heidi-Leitfaden-f-r-den-Einstieg-288ca630286e8078bd45c3f225dc72ae';

export interface SideNavFooterUser {
  firstName: string | null | undefined;
  lastName: string | null | undefined;
  email: string | null | undefined;
  profilePictureUrl: string | null | undefined;
  shouldHideSensitiveInfo: boolean | undefined;
}

export interface SideNavFooterProps {
  isMobile: boolean;
  isSidebarOpen: boolean;
  user?: SideNavFooterUser;
  isGlobalAdmin?: boolean;
  teamName?: string;
  closeSidePanels: () => void;
}

interface CpdAccountMenuItemProps {
  closeSidePanels: () => void;
  dismissMobileSidebar: () => void;
  isOfflineGated: boolean;
  locale: string;
  offlineTooltip: string;
}

const CpdAccountMenuItem = memo(function CpdAccountMenuItem({
  closeSidePanels,
  dismissMobileSidebar,
  isOfflineGated,
  locale,
  offlineTooltip,
}: CpdAccountMenuItemProps) {
  const router = useRouter();
  const intl = useIntl();
  const { term: cpdTerm } = useCPDTerm(useIsCpdUsUser());
  const isCpdQueryEnabled = useIsCpdQueryEnabled();
  const { data: cpdStats } = useQueryCPDStats({ isCpdQueryEnabled });
  const cpdSourcesCount = cpdStats?.total_sources ?? 0;
  const cpdSourcesBadgeLabel = useMemo(
    () =>
      new Intl.NumberFormat(locale, {
        notation: 'compact',
        maximumFractionDigits: 1,
      }).format(cpdSourcesCount),
    [locale, cpdSourcesCount]
  );
  const cpdMenuItemAriaLabel = intl.formatMessage(
    sideNavFooterMessages.cpdMenuItemAriaLabel,
    {
      cpdTerm,
      count: cpdSourcesCount,
    }
  );

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        {/* Wrap in a span so hover reaches the tooltip: a disabled
            DropdownMenuItem sets `pointer-events-none`, which would otherwise
            swallow the hover on the trigger (see AppearanceModeMenuItems). */}
        <span className="block">
          <DropdownMenuItem
            disabled={isOfflineGated}
            onClick={
              isOfflineGated
                ? undefined
                : () => {
                    closeSidePanels();
                    dismissMobileSidebar();
                    void router.push('/evidence/cpd');
                  }
            }
            aria-label={cpdMenuItemAriaLabel}
            data-testid="sidebar-menu-cpd"
            title={isOfflineGated ? offlineTooltip : undefined}
          >
            <GraduationCap />
            <span className="flex-1">{cpdTerm}</span>
            <Badge color="neutral" size="xs" className="shrink-0 tabular-nums">
              {cpdSourcesBadgeLabel}
            </Badge>
          </DropdownMenuItem>
        </span>
      </TooltipTrigger>
      {isOfflineGated && <TooltipContent>{offlineTooltip}</TooltipContent>}
    </Tooltip>
  );
});

export const SideNavFooter = memo(
  ({ isMobile, user, isGlobalAdmin, teamName, closeSidePanels }: SideNavFooterProps) => {
    const intl = useIntl();
    const { locale } = intl;
    const supportedLocale = mapLocaleAliasToSupportedLocale(locale);
    const router = useRouter();
    const setIsMobileHeaderSheetOpen = useSetAtom(isMobileHeaderSheetOpenAtom);
    const setIsGetMobileAppModalOpen = useSetAtom(isGetMobileAppModalOpenAtom);
    // Atom-backed notifications popover open state. Account-menu items below
    // close it imperatively via `setNotificationsOpen(false)`; card action
    // clicks close it via the `NotificationsPopoverControlContext`
    // (see `NotificationsPopoverContent.onRequestClose`). The atom is also
    // written by `useCollapsedFooterDescriptors` when the Notifications row is
    // selected from the primary nav More (sidebar IA, small viewport).
    const [isNotificationsOpen, setNotificationsOpen] = useAtom(
      notificationsPopoverOpenAtom
    );
    const closeNotifications = useCallback(
      () => setNotificationsOpen(false),
      [setNotificationsOpen]
    );
    // Read HERE and passed down, because `useEffectiveSessionTimeout` is not a
    // flag: it merges `GET /user` with a user-scoped localStorage override and
    // writes that override back, so it stays in the settings tree it belongs to
    // and its value crosses into the packaged compliance hooks as a parameter.
    //
    // `?? null` collapses the hook's `| undefined` arm, which
    // `resolveEffectiveSessionTimeout` declares but cannot return — every one of
    // its paths yields a value or `null`. The package seam takes
    // `EffectiveSessionTimeout | null` so its own hooks have one absent case to
    // reason about instead of two that mean the same thing.
    const effectiveSessionTimeout = useEffectiveSessionTimeout() ?? null;
    // PostHog-resolved, because `@heidi/growth/flags` has no PostHog surface.
    // Crossed as values the same way the timeout already is.
    const flagGates = useComplianceNotificationFlagGates();
    useAutoOpenComplianceNotificationsOnLogin({
      effectiveSessionTimeout,
      flagGates,
      enabled: !isMobile,
      isNotificationsOpen,
      setNotificationsOpen,
    });
    const setIsEvidenceReferralModalOpen = useSetAtom(isEvidenceReferralModalOpenAtom);
    const { closePanel } = useMobilePanel();
    const { showEvidenceFocusedNav } = useEvidenceNavEnabled();
    const { open: openSettingsModal } = useSettingsModal();
    const showAccountMenuCpd = useIsCpdAccountMenuShown();
    const isWorkspaceSettingsIaEnabled = useIsWorkspaceSettingsIaEnabled();
    const { shouldGateControls: isOfflineGated } = useOfflineGating();
    const offlineTooltip = intl.formatMessage(offlineGatingMessages.notAvailableOffline);

    const dismissMobileSidebar = useCallback(() => {
      if (isMobile) {
        closePanel();
        setIsMobileHeaderSheetOpen(false);
      }
    }, [isMobile, closePanel, setIsMobileHeaderSheetOpen]);

    // User display data for UserAccountMenu
    const userName = user
      ? (supportedLocale && isCjkLocale(supportedLocale)
          ? [user.lastName, user.firstName]
          : [user.firstName, user.lastName]
        )
          .filter(Boolean)
          .join(' ') || intl.formatMessage(sideNavFooterMessages.defaultUserName)
      : intl.formatMessage(sideNavFooterMessages.defaultUserName);
    const userEmail =
      user?.shouldHideSensitiveInfo || !user?.email
        ? teamName || ''
        : teamName || user.email;
    const userAvatar = user?.profilePictureUrl || '';
    // Raw first/last (not `userName`) keeps the colour off the translated
    // placeholder and CJK reorder; email is the name-less fallback.
    const userAvatarColorSeed =
      [user?.firstName, user?.lastName].filter(Boolean).join(' ') || user?.email || '';
    const isRibbonSidebarEnabled = useIsRibbonSidebarEnabled();
    // One shared <ul> for the ribbon rail (hoisted below); every footer item
    // reads the same useIsRibbonRail() so the wrap-vs-bare-<li> decision can't
    // drift between parent and children.
    const isRibbonRail = useIsRibbonRail();
    const isFooterOverflowEnabled = isRibbonSidebarEnabled && isRibbonRail;
    // GRO-2981: widen the avatar dropdown only while the Remote intro promo
    // card is actually going to render — the card's title row (title +
    // "Offer" badge + close X) overflows the default 224px floor. Once the
    // user dismisses or engages, the popover returns to its default width.
    const isRemoteIntroPromoMenuCardVisible = useIsRemoteIntroPromoMenuCardVisible();
    const isSidebarIAEnabled = useIsSidebarIAEnabled();

    // Footer overflow: Get Started and Evidence Referral collapse into the More
    // in ribbon icon mode. They self-hide below and register a descriptor.
    // Shared by the inline tile and the collapsed row so there's a single
    // emit-host call site.
    const handleGetStartedFaqClick = useCallback(() => {
      navigationGetStartedFAQ();
      dismissMobileSidebar();
    }, [dismissMobileSidebar]);
    const getStartedDescriptor = useMemo<FooterMoreRowDescriptor>(
      () => ({
        id: 'getStarted',
        label: intl.formatMessage(sideNavFooterMessages.getStartedFaq),
        icon: <BookOpenIcon />,
        testId: 'sidebar-link-get-started-faq',
        href: GET_STARTED_FAQ_URL,
        external: true,
        onSelect: handleGetStartedFaqClick,
      }),
      [intl, handleGetStartedFaqClick]
    );

    const evidenceDescriptor = useMemo<FooterMoreRowDescriptor>(
      () => ({
        id: 'evidence',
        label: intl.formatMessage(sideNavFooterMessages.referAndEarn),
        icon: <Gift />,
        testId: 'sidebar-evidence-referral-cta',
        onSelect: () => {
          setIsEvidenceReferralModalOpen(true);
          dismissMobileSidebar();
        },
      }),
      [intl, setIsEvidenceReferralModalOpen, dismissMobileSidebar]
    );

    // On the ribbon rail these render as bare <li> inside the shared <ul> below;
    // other modes give each its own SidebarMenu. Help/Notifications are pinned and
    // always render, so the shared <ul> is never empty.
    const footerNavGroup = (
      <>
        {/* Collapsible group — the More sits at its top; items fold up into it.
            Renders nothing when everything fits. Refer, Get Started, Discovery,
            Evidence, Integrations, then Remote/Dictate (in the icon strip). */}
        {/* Sidebar IA: no footer More — Dictate collapses to primary nav More instead. */}
        {!isSidebarIAEnabled && <FooterRailMore />}

        {/* Sidebar IA: Refer-and-earn removed when flag is on (MUL-3702). */}
        {!isSidebarIAEnabled && <ReferralButton onOpen={dismissMobileSidebar} />}

        {/* German "Get Started FAQ" — Sidebar IA: removed when flag is on (MUL-3702). */}
        {!isSidebarIAEnabled && (
          <FooterRailCollapsibleItem
            shouldShow={Boolean(IS_GERMAN_GET_STARTED_CTA())}
            descriptor={getStartedDescriptor}
          />
        )}

        {/* Discovery Call: collapses into footer More (IA off) or primary nav More
            (IA on, cascade position 1) via useFooterRailCollapsible inside the component. */}
        <DiscoveryCallNavItem onAfterClick={dismissMobileSidebar} />

        {/* Evidence Referral CTA — Sidebar IA: removed when flag is on (MUL-3702). */}
        {!isSidebarIAEnabled && (
          <FooterRailCollapsibleItem
            shouldShow={Boolean(
              showEvidenceFocusedNav && IS_EVIDENCE_LEADERBOARD_ENABLED()
            )}
            descriptor={evidenceDescriptor}
          />
        )}

        {/* Integrations nav item — experiment gated by exp-integration-button-in-sidebar.
            Sidebar IA: hidden when flag is on (MUL-3702, Connectors removed). */}
        {!isSidebarIAEnabled && <IntegrationsNavItem />}

        {/*
          Help & Notifications icon buttons (Remote/Dictate ride in this strip
          too — the tail of the collapsible group; Help/Notif are pinned).

          Desktop: the notifications Popover Root is hoisted to here so
          the bell (rendered deep inside `<FooterIconButtons />` across
          its three modes — label-icons, vertical NavItem, horizontal)
          and the popover content share a common ancestor.
          `<PopoverTrigger asChild>` wraps the bell in each mode so the
          popover positions off it precisely. Popover is non-modal — no
          scroll lock, no focus trap, intentional. Radix Popover Root emits
          no in-flow DOM, so it is safe inside the shared <ul>.

          Mobile: the bell always opens the mobile panel sheet via
          `useMobilePanel`. We deliberately skip the Popover Root so we
          don't mount `NotificationsPopoverContent` off-screen (its
          query hooks are non-trivial) and so the bell never
          accidentally wires as a popover trigger.
        */}
        {!isMobile ? (
          <Popover open={isNotificationsOpen} onOpenChange={setNotificationsOpen}>
            <FooterIconButtons isMobile={isMobile} />
            <NotificationsPopoverContent onRequestClose={closeNotifications} />
          </Popover>
        ) : (
          <FooterIconButtons isMobile={isMobile} />
        )}
      </>
    );

    return (
      <FooterRailProvider
        enabled={isFooterOverflowEnabled}
        className={cn(
          'flex flex-col items-center gap-2',
          isRibbonSidebarEnabled && [
            'gap-2',
            // Consistent spacing between footer items (notifications, help, earn days, user)
            '[&>*]:py-0',
          ]
        )}
      >
        {!isRibbonSidebarEnabled && <Separator />}
        {!isRibbonSidebarEnabled && <TrialCountdownButton />}

        {/* Keep gap-2: it drives both the icon spacing and the overflow budget's
            8px reserve, so the two must not drift. */}
        {isRibbonRail ? (
          <SidebarMenu className="relative items-center gap-2">
            {footerNavGroup}
          </SidebarMenu>
        ) : (
          footerNavGroup
        )}

        {/* Pinned CTAs sit below the collapsible group (never collapse). */}
        {IS_MD_WIDGET_LAUNCH_ENABLED() && <MedicalDirectorWidgetLaunchButton />}

        <UpgradeCTAButton location={'sidebar'} />

        {isRibbonSidebarEnabled && <TrialCountdownButton />}

        <SideNavFooterUpgradeSlot />

        {/* User Account Menu — extra top margin in new sidebar to double spacing from gift icon */}
        {user && (
          <UserAccountMenu
            user={{
              name: userName,
              email: userEmail,
              avatar: userAvatar,
            }}
            colorSeed={userAvatarColorSeed}
            avatarOnlyTrigger={isRibbonSidebarEnabled && !isMobile}
            sideOffset={isRibbonSidebarEnabled && !isMobile ? 12 : 4}
            className={cn(isRibbonSidebarEnabled && 'mt-2')}
            contentClassName={cn(isRemoteIntroPromoMenuCardVisible && 'min-w-72')}
            appearanceMenuSlot={<AppearanceModeMenuItems />}
            data-testid="sidebar-user-account-menu"
          >
            {IS_GET_HEIDI_FOR_MOBILE_ENABLED() && (
              <Tooltip>
                <TooltipTrigger asChild>
                  {/* Wrap in a span so hover reaches the tooltip while the
                      item is disabled (pointer-events-none). */}
                  <span className="block">
                    <DropdownMenuItem
                      disabled={isOfflineGated}
                      onClick={
                        isOfflineGated
                          ? undefined
                          : () => {
                              setIsGetMobileAppModalOpen(true);
                              dismissMobileSidebar();
                            }
                      }
                      data-testid="sidebar-menu-get-mobile-app"
                      title={isOfflineGated ? offlineTooltip : undefined}
                    >
                      <Smartphone />
                      <FormattedMessage {...sideNavFooterMessages.getMobileApp} />
                    </DropdownMenuItem>
                  </span>
                </TooltipTrigger>
                {isOfflineGated && <TooltipContent>{offlineTooltip}</TooltipContent>}
              </Tooltip>
            )}
            {!isWorkspaceSettingsIaEnabled && (
              <Tooltip>
                <TooltipTrigger asChild>
                  {/* Wrap in a span so hover reaches the tooltip while the
                      item is disabled (pointer-events-none). */}
                  <span className="block">
                    <DropdownMenuItem
                      disabled={isOfflineGated}
                      onClick={
                        isOfflineGated
                          ? undefined
                          : () => {
                              closeSidePanels();
                              dismissMobileSidebar();
                              void router.push(isGlobalAdmin ? '/organization' : '/team');
                            }
                      }
                      data-testid="sidebar-menu-team"
                      title={isOfflineGated ? offlineTooltip : undefined}
                    >
                      <Building />
                      {isGlobalAdmin ? (
                        <FormattedMessage {...sideNavFooterMessages.organization} />
                      ) : (
                        <FormattedMessage {...sideNavFooterMessages.team} />
                      )}
                    </DropdownMenuItem>
                  </span>
                </TooltipTrigger>
                {isOfflineGated && <TooltipContent>{offlineTooltip}</TooltipContent>}
              </Tooltip>
            )}
            {showAccountMenuCpd ? (
              <CpdAccountMenuItem
                closeSidePanels={closeSidePanels}
                dismissMobileSidebar={dismissMobileSidebar}
                isOfflineGated={isOfflineGated}
                locale={locale}
                offlineTooltip={offlineTooltip}
              />
            ) : null}
            <SessionLayoutV2MenuToggle
              isOfflineGated={isOfflineGated}
              offlineTooltip={offlineTooltip}
            />
            <Tooltip>
              <TooltipTrigger asChild>
                {/* Wrap in a span so hover reaches the tooltip while the
                    item is disabled (pointer-events-none). */}
                <span className="block">
                  <DropdownMenuItem
                    disabled={isOfflineGated}
                    onClick={
                      isOfflineGated
                        ? undefined
                        : () => {
                            closeSidePanels();
                            closeNotifications();
                            dismissMobileSidebar();
                            // No explicit href on either platform: resume the
                            // user's last-viewed section (`lastSettingsRouteAtom`,
                            // defaults to `/settings/account` on first ever open).
                            // Mobile additionally lands on the sidebar list rather
                            // than the resumed section — `useSettingsModal#open`
                            // keys that off `href === undefined`.
                            openSettingsModal(undefined, 'sidebar_footer');
                          }
                    }
                    data-testid="sidebar-menu-settings"
                    title={isOfflineGated ? offlineTooltip : undefined}
                  >
                    <Settings />
                    <FormattedMessage {...sideNavFooterMessages.settings} />
                  </DropdownMenuItem>
                </span>
              </TooltipTrigger>
              {isOfflineGated && <TooltipContent>{offlineTooltip}</TooltipContent>}
            </Tooltip>
            <DropdownMenuItem
              onClick={() => {
                dismissMobileSidebar();
                void logout();
                navigationLogout();
              }}
              data-testid="sidebar-menu-logout"
            >
              <LogOut />
              <FormattedMessage {...sideNavFooterMessages.logOut} />
            </DropdownMenuItem>
            {/* GRO-2981: Heidi Remote intro promo card. Self-gating on
                eligibility — renders null when not applicable. Sits at the
                bottom of the dropdown per Figma (node 11164:12769). */}
            <RemoteIntroPromoMenuCard onAfterClick={dismissMobileSidebar} />
          </UserAccountMenu>
        )}

        {/* GRO-2981: Remote intro promo modal (triggered from Billing banner
            or the promo menu card above). Mounted here so it's available
            app-wide regardless of which surface opened it. */}
        <RemoteIntroPromoModal />
      </FooterRailProvider>
    );
  }
);

SideNavFooter.displayName = 'SideNavFooter';
